// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tabs,
  Tab,
  Box,
  TextField,
  Button,
  Alert,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AppearancePanel } from './AppearancePanel';

export const ProfileModal = ({
  isOpen,
  onClose,
  user,
  apiUrl,
  onUserUpdate,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [displayName, setDisplayName] = useState('');

  // Password form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Messages
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');

  // Load user data when modal opens
  useEffect(() => {
    if (isOpen && user) {
      setDisplayName(user.display_name || '');
      clearMessages();
    }
  }, [isOpen, user]);

  // Reset to first tab only when modal first opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(0);
    }
  }, [isOpen]);

  const clearMessages = () => {
    setProfileError('');
    setProfileSuccess('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleClose = () => {
    clearMessages();
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    onClose();
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    clearMessages();

    const token = localStorage.getItem('cme_token');
    try {
      const res = await fetch(`${apiUrl}/users/me`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ display_name: displayName })
      });

      const data = await res.json();

      if (res.ok) {
        setProfileSuccess('Profile updated successfully!');
        onUserUpdate({ ...user, display_name: displayName });
      } else {
        setProfileError(data.detail || 'Failed to update profile');
      }
    } catch (err) {
      setProfileError('Network error. Please try again.');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    clearMessages();

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    const token = localStorage.getItem('cme_token');
    try {
      const res = await fetch(`${apiUrl}/users/me/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await res.json();

      if (res.ok) {
        setPasswordSuccess('Password changed successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setPasswordError(data.detail || 'Failed to change password');
      }
    } catch (err) {
      setPasswordError('Network error. Please try again.');
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Settings
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="General" />
          <Tab label="Password" />
          <Tab label="Appearance" />
        </Tabs>
      </Box>

      <DialogContent>
        {/* General Tab */}
        {activeTab === 0 && (
          <Box component="form" onSubmit={handleUpdateProfile}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Email
              </Typography>
              <Typography variant="body1">{user?.email}</Typography>
            </Box>

            <TextField
              fullWidth
              label="Display Name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              margin="normal"
              size="small"
            />

            {profileError && <Alert severity="error" sx={{ mt: 2 }}>{profileError}</Alert>}
            {profileSuccess && <Alert severity="success" sx={{ mt: 2 }}>{profileSuccess}</Alert>}

            <Button type="submit" variant="contained" sx={{ mt: 2 }}>
              Update Profile
            </Button>
          </Box>
        )}

        {/* Password Tab */}
        {activeTab === 1 && (
          <Box component="form" onSubmit={handleChangePassword}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              margin="normal"
              size="small"
              required
            />

            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              margin="normal"
              size="small"
              required
              inputProps={{ minLength: 6 }}
            />

            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              margin="normal"
              size="small"
              required
              inputProps={{ minLength: 6 }}
            />

            {passwordError && <Alert severity="error" sx={{ mt: 2 }}>{passwordError}</Alert>}
            {passwordSuccess && <Alert severity="success" sx={{ mt: 2 }}>{passwordSuccess}</Alert>}

            <Button type="submit" variant="contained" sx={{ mt: 2 }}>
              Change Password
            </Button>
          </Box>
        )}

        {/* Appearance Tab */}
        {activeTab === 2 && <AppearancePanel />}
      </DialogContent>
    </Dialog>
  );
};
